<template>
  <Teleport to="body">
    <transition name="lightbox-fade">
      <div
        v-if="isOpen && currentImage"
        ref="lightboxRef"
        class="lightbox__dialog"
        role="dialog"
        aria-modal="true"
        @click.self="handleClose"
      >
        <button class="lightbox__close" aria-label="Sluit vergrote afbeelding" @click="handleClose()">
          <AppIconLoader name="smallen" variant="medium" role="presentation" aria-hidden="true" />
        </button>

        <figure class="lightbox__figure">
          <img
            ref="imgRef"
            v-panzoom="{
              minScale: 1,
              maxScale: 6,
              contain: 'none',
              overflow: 'visible',
              animate: true,
              duration: 250,
              ease: 'ease-in-out',
              step: 0.3,
              zoomStyle: 'transform',
              panOnlyWhenZoomed: true,
            }"
            class="lightbox__img"
            :src="currentImage.src"
            :alt="currentImage.alt || ''"
            draggable="false"
          >
          <figcaption v-if="currentImage.caption" class="lightbox__caption">
            {{ currentImage.caption }}
          </figcaption>
        </figure>
      </div>
    </transition>
  </Teleport>
</template>

<script setup lang="ts">
import { onMounted, watch, onBeforeUnmount, ref } from 'vue'
import { useLightbox } from '@/composables/useLightbox'

const imgRef = ref<HTMLImageElement | null>(null)
const lightboxRef = ref<HTMLElement | null>(null)
const { isOpen, currentImage, close } = useLightbox()

useFocusTrap(lightboxRef, isOpen)

let pz: any | null = null
const STEP = 2 // target scale when zooming in (change to taste)
const DOUBLE_TAP_MS = 300 // max gap
const MAX_TAP_MOVE = 8 // px, to still count as a tap
const MAX_TAP_SEP = 60 // px, taps close enough

let lastTapAt = 0
let lastTapX = 0
let lastTapY = 0
let downX = 0
let downY = 0
let touchPointers = 0
let didPinch = false

const onDblClick = (e: MouseEvent) => {
  if (!pz) { return }
  e.preventDefault()
  const target = pz.getScale() <= 1.001 ? STEP : 1
  if (target === 1) {
    pz.reset()
  } else {
    pz.zoomToPoint(target, { clientX: e.clientX, clientY: e.clientY }, { animate: true })
  }
}

const onPointerDown = (e: PointerEvent) => {
  if (e.pointerType !== 'touch') { return }
  touchPointers++
  if (touchPointers >= 2) { didPinch = true } // any two-finger interaction cancels double-tap
  downX = e.clientX
  downY = e.clientY
}

const onPointerUp = (e: PointerEvent) => {
  if (!pz || e.pointerType !== 'touch') { return }

  // reduce pointer count and clear pinch flag when all fingers are up
  if (touchPointers > 0) { touchPointers-- }
  if (touchPointers === 0 && didPinch) {
    didPinch = false
  }

  // must be a tap (not a drag)
  const moved = Math.hypot(e.clientX - downX, e.clientY - downY)
  if (moved > MAX_TAP_MOVE || didPinch) { return }

  const now = performance.now()
  if (now - lastTapAt < DOUBLE_TAP_MS && Math.hypot(e.clientX - lastTapX, e.clientY - lastTapY) < MAX_TAP_SEP) {
    // second tap: toggle zoom at the finger position
    e.preventDefault() // needs { passive:false } on the listener
    const target = pz.getScale() <= 1.001 ? STEP : 1
    if (target === 1) {
      pz.reset({ animate: true }) // tidy 1× recentre
    } else {
      pz.zoomToPoint(target, { clientX: e.clientX, clientY: e.clientY }, { animate: true })
    }
    lastTapAt = 0 // reset sequence
  } else {
    // first tap
    lastTapAt = now
    lastTapX = e.clientX
    lastTapY = e.clientY
  }
}

const onPointerCancel = () => {
  touchPointers = 0
  didPinch = false
}

const handleClose = () => {
  close()
}

const onKey = (e: KeyboardEvent) => {
  if (!isOpen.value) { return }
  if (e.key === 'Escape') {
    close()
  }
}

const attach = () => {
  const el = imgRef.value
  if (!el) { return }
  // Panzoom instance is created by the directive on mount of <img>

  pz = (el as any).__panzoom__ || null
  if (!pz) { return } // directive not ready yet; the watcher will run again nextTick
  el.addEventListener('dblclick', onDblClick, { passive: false })

  el.addEventListener('pointerdown', onPointerDown, { passive: true })
  el.addEventListener('pointerup', onPointerUp, { passive: false }) // <-- must be passive:false
  el.addEventListener('pointercancel', onPointerCancel)
}

const detach = () => {
  const el = imgRef.value
  if (!el) { return }
  el.removeEventListener('dblclick', onDblClick)
  el.removeEventListener('pointerdown', onPointerDown)
  el.removeEventListener('pointerup', onPointerUp)
  el.removeEventListener('pointercancel', onPointerCancel)
}

// attach listeners when an image actually renders; detach when hidden/changed
watch(
  [isOpen, currentImage],
  async ([open, img]) => {
    detach()
    if (open && img) {
      await nextTick() // wait for <img> to render and directive to run
      // try twice in case directive attaches instance microtask-late
      attach()
      if (!pz) {
        await nextTick()
        attach()
      }
    }
  },
  { immediate: true }
)

onMounted(() => {
  window.addEventListener('keydown', onKey)

  detach()
})

onBeforeUnmount(() => {
  window.removeEventListener('keydown', onKey)

  const el = imgRef.value
  if (el) {
    el.removeEventListener('dblclick', onDblClick)
  }
  pz = null
})
</script>

<style scoped lang="postcss">
.lightbox {
  &__dialog {
    position: fixed;
    inset: 0;
    display: grid;
    place-items: center;
    background: var(--background-color);
    backdrop-filter: blur(12px);
    z-index: 9999;
    pointer-events: all;
  }

  &__close {
    right: 0.5rem;
    top: 0.5rem;
    background: unset;
    border: unset;
    padding: unset;
    z-index: 3;
    position: absolute;
    cursor: pointer;

    @media (--md) {
      right: 0.75rem;
      top: 0.75rem;
    }

    @media (--lg) {
      right: 1rem;
      top: 1rem;
    }

    > .icon-loader__component {
      display: block;
    }
  }

  &__figure {
    max-width: 95vw;
    margin: 0;
    padding: 0;
    overflow: visible;
  }

  &__img {
    max-width: 100%;
    max-height: 95vh;
    object-fit: contain;
    /* needed for stable clientWidth/Height */
    touch-action: none;
    user-select: none;
  }

  &__caption {
    color: #eee;
    text-align: center;
    font-size: 0.95rem;
  }
}

/* remove the :deep(.lightbox-img) animation block entirely */
/* fade + subtle pop on the FIGURE instead */
.lightbox-fade-enter-active,
.lightbox-fade-leave-active {
  transition: opacity 250ms ease;
}

.lightbox-fade-enter-from,
.lightbox-fade-leave-to {
  opacity: 0;
}

.lightbox-fade-enter-active .lightbox__figure {
  transition: transform 500ms ease;
}

.lightbox-fade-enter-from .lightbox__figure {
  transform: scale(0.5);
}
</style>
